import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '@/services/api';
import { liveSongRequestWebSocketService } from '@/services/liveSongRequestWebSocket';
import { searchYouTubeVideos, getYouTubeWatchUrl, getYouTubeEmbedUrl, type YouTubeSearchItem } from '@/services/youtubeApi';
import styles from './LiveSongRequestPanel.module.scss';

interface ArrivedBooking {
  id: string;
  tableName?: string;
}

interface LiveSongRequest {
  id: string;
  status: string;
  songDescription?: string;
  tableName?: string;
  bookingId?: string;
  createdAt?: string;
  commentsEnabled?: boolean;
  /** Customer display name (from API current request). */
  displayName?: string;
  /** Customer profile picture URL (from API current request). */
  profilePic?: string;
  /** When true, request is from organizer; use organizationName and organizerLogoUrl. */
  isOrganizer?: boolean;
  is_organizer?: boolean;
  organizationName?: string;
  organization_name?: string;
  organizerLogoUrl?: string;
  organizer_logo_url?: string;
  [key: string]: any;
}

interface Comment {
  id: string;
  commentText: string;
  createdAt?: string;
  senderType?: string;
  imageUrls?: string[];
  [key: string]: any;
}

interface LiveSongRequestPanelProps {
  branchId: string;
  /** When true, show a message instead of nothing when not available (e.g. inside a modal). */
  inModal?: boolean;
  /** Called when a song request is successfully submitted (e.g. to close the modal). */
  onRequestSuccess?: () => void;
}

/** Unwrap API response to payload (data may be in res.data or res). */
function unwrapData<T>(res: { data?: T } | T): T | undefined {
  if (res == null) return undefined;
  if (typeof res === 'object' && 'data' in res && res.data !== undefined) return res.data as T;
  return res as T;
}

const REQUESTS_PAGE_LIMIT = 20;
const COMMENT_IMAGES_MAX = 10;
const COMMENT_IMAGE_MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50MB (images and videos)

/** Extract YouTube video ID from common URL formats. */
function getYouTubeVideoId(url: string): string | null {
  if (!url || !url.trim()) return null;
  const u = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=)([a-zA-Z0-9_-]{11})/,
    /(?:youtu\.be\/)([a-zA-Z0-9_-]{11})/,
    /(?:youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const re of patterns) {
    const m = u.match(re);
    if (m) return m[1];
  }
  return null;
}

export default function LiveSongRequestPanel({ branchId, inModal, onRequestSuccess }: LiveSongRequestPanelProps) {
  const [available, setAvailable] = useState<boolean>(false);
  const [commentsEnabled, setCommentsEnabled] = useState<boolean>(false);
  const [arrivedBookings, setArrivedBookings] = useState<ArrivedBooking[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState<boolean>(true);
  const [requests, setRequests] = useState<LiveSongRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [form, setForm] = useState({
    bookingId: '',
    songDescription: 'Song', // Predefined default when customer doesn't enter
    sourceLink: '',
    customerComments: '',
    fileUrl: '',
    photoUrl: ''
  });
  const [requestGroupPhoto, setRequestGroupPhoto] = useState<File | null>(null);
  const [groupPhotoPreviewUrl, setGroupPhotoPreviewUrl] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [sourceType, setSourceType] = useState<'link' | 'file'>('link');
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [sourceFilePreviewUrl, setSourceFilePreviewUrl] = useState<string | null>(null);
  const sourceFileInputRef = React.useRef<HTMLInputElement>(null);
  const [youtubeSearchQuery, setYoutubeSearchQuery] = useState('');
  const [youtubeSearchResults, setYoutubeSearchResults] = useState<YouTubeSearchItem[]>([]);
  const [youtubeSearchLoading, setYoutubeSearchLoading] = useState(false);
  const [youtubeSearchError, setYoutubeSearchError] = useState<string | null>(null);
  const [selectedYouTubeVideo, setSelectedYouTubeVideo] = useState<YouTubeSearchItem | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [comments, setComments] = useState<Record<string, Comment[]>>({});
  const [loadingComments, setLoadingComments] = useState<Record<string, boolean>>({});
  const [newCommentText, setNewCommentText] = useState<Record<string, string>>({});
  const [postingComment, setPostingComment] = useState<Record<string, boolean>>({});
  const [requestAgainLoading, setRequestAgainLoading] = useState<Record<string, boolean>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentRequest, setCurrentRequest] = useState<LiveSongRequest | null>(null);
  const [myRequest, setMyRequest] = useState<LiveSongRequest | null>(null);
  const [loadingCurrent, setLoadingCurrent] = useState<boolean>(false);
  const [showSendCommentsPanel, setShowSendCommentsPanel] = useState<boolean>(false);
  const [selectedRequestIdForComments, setSelectedRequestIdForComments] = useState<string | null>(null);
  const [currentCommentText, setCurrentCommentText] = useState<string>('');
  const [currentCommentImages, setCurrentCommentImages] = useState<File[]>([]);
  const [currentCommentImagePreviewUrls, setCurrentCommentImagePreviewUrls] = useState<string[]>([]);
  const currentCommentImagesInputRef = React.useRef<HTMLInputElement>(null);
  const [postingCurrentComment, setPostingCurrentComment] = useState<boolean>(false);
  const [commentSentForReview, setCommentSentForReview] = useState<boolean>(false);
  const [currentCommentImageError, setCurrentCommentImageError] = useState<string | null>(null);
  const [showSlideshowPreview, setShowSlideshowPreview] = useState<boolean>(false);
  const [slideshowPreviewIndex, setSlideshowPreviewIndex] = useState<number>(0);
  const [showSendCommentsPopup, setShowSendCommentsPopup] = useState<boolean>(false);
  const [commentsPopupTextOnly, setCommentsPopupTextOnly] = useState<boolean>(false);

  const fetchAvailable = useCallback(async () => {
    if (!branchId) return;
    setLoadingAvailable(true);
    try {
      const [availRes, seatsRes] = await Promise.all([
        apiService.getLiveSongAvailable(branchId),
        apiService.getMyBookingSeats().catch(() => null),
      ]);
      const data = unwrapData<{ available?: boolean; arrivedBookings?: ArrivedBooking[]; commentsEnabled?: boolean }>(availRes);
      setCommentsEnabled(Boolean(data?.commentsEnabled));
      const bookings = Array.isArray(data?.arrivedBookings) ? data.arrivedBookings : [];

      const seatsData = seatsRes ? unwrapData<{ seats?: any[] }>(seatsRes) : null;
      const seats = Array.isArray(seatsData?.seats) ? seatsData.seats : [];
      const chairBookings: ArrivedBooking[] = seats
        .filter((s: any) => {
          if ((s.status || '').toLowerCase() !== 'approved') return false;
          const b = s.booking;
          return b && b.branchId === branchId && ['active', 'confirmed'].includes((b.status || '').toLowerCase());
        })
        .map((s: any) => ({
          id: s.bookingId || s.booking?.id,
          tableName: s.booking?.table?.tableName || s.booking?.table?.table_name || `Table ${s.booking?.table?.tableNumber ?? ''}`.trim(),
        }));

      const merged = [...bookings];
      for (const cb of chairBookings) {
        if (cb.id && !merged.some((b) => b.id === cb.id)) {
          merged.push(cb);
        }
      }

      setAvailable(Boolean(data?.available) || merged.length > 0);
      setArrivedBookings(merged);
      if (merged[0]?.id) {
        setForm((f) => (f.bookingId ? f : { ...f, bookingId: merged[0].id }));
      }
    } catch (e) {
      setAvailable(false);
      setArrivedBookings([]);
    } finally {
      setLoadingAvailable(false);
    }
  }, [branchId]);

  const fetchRequests = useCallback(async (pageNum: number) => {
    if (!branchId) return;
    setLoadingRequests(true);
    try {
      const res = await apiService.getLiveSongRequests(branchId, {
        page: pageNum,
        limit: REQUESTS_PAGE_LIMIT
      });
      const data = unwrapData<{ requests?: any[]; total?: number } | any[]>(res);
      const list = Array.isArray(data) ? data : (Array.isArray((data as any)?.requests) ? (data as any).requests : []);
      setRequests(list);
      if (data && typeof data === 'object' && 'total' in data && typeof (data as any).total === 'number') {
        setTotal((data as any).total);
      }
    } catch {
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [branchId]);

  const fetchComments = useCallback(async (requestId: string) => {
    setLoadingComments((prev) => ({ ...prev, [requestId]: true }));
    try {
      const res = await apiService.getLiveSongRequestComments(requestId);
      const data = unwrapData<{ comments?: any[] }>(res);
      setComments((prev) => ({ ...prev, [requestId]: Array.isArray(data?.comments) ? data.comments : [] }));
    } catch {
      setComments((prev) => ({ ...prev, [requestId]: [] }));
    } finally {
      setLoadingComments((prev) => ({ ...prev, [requestId]: false }));
    }
  }, []);

  const fetchCurrentRequest = useCallback(async () => {
    if (!branchId) return;
    setLoadingCurrent(true);
    try {
      const res = await apiService.getLiveSongCurrentRequest(branchId);
      const raw = unwrapData<any>(res);

      let currentSong: LiveSongRequest | null = null;
      let myReq: LiveSongRequest | null = null;

      if (raw && typeof raw === 'object') {
        const cur = raw.current ?? raw.request;
        if (cur && typeof cur === 'object' && cur.id) {
          currentSong = cur as LiveSongRequest;
        }
        const my = raw.myRequest;
        if (my && typeof my === 'object' && my.id) {
          myReq = my as LiveSongRequest;
        }
        if (!currentSong && !myReq && raw.id) {
          currentSong = raw as LiveSongRequest;
        }
      }

      setCurrentRequest(currentSong);
      setMyRequest(myReq);
      if (currentSong?.id) fetchComments(currentSong.id);
      if (myReq?.id && myReq.id !== currentSong?.id) fetchComments(myReq.id);
    } catch {
      setCurrentRequest(null);
      setMyRequest(null);
    } finally {
      setLoadingCurrent(false);
    }
  }, [branchId, fetchComments]);

  useEffect(() => {
    return () => {
      if (groupPhotoPreviewUrl) URL.revokeObjectURL(groupPhotoPreviewUrl);
      if (sourceFilePreviewUrl) URL.revokeObjectURL(sourceFilePreviewUrl);
    };
  }, [groupPhotoPreviewUrl, sourceFilePreviewUrl]);

  useEffect(() => {
    setCurrentCommentImagePreviewUrls((prev) => {
      prev.forEach((u) => URL.revokeObjectURL(u));
      return currentCommentImages.map((f) => URL.createObjectURL(f));
    });
  }, [currentCommentImages]);

  useEffect(() => {
    return () => currentCommentImagePreviewUrls.forEach((u) => URL.revokeObjectURL(u));
  }, [currentCommentImagePreviewUrls]);

  useEffect(() => {
    if (!showSlideshowPreview) return;
    const onEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowSlideshowPreview(false); };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [showSlideshowPreview]);

  const handleGroupPhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (groupPhotoPreviewUrl) {
      URL.revokeObjectURL(groupPhotoPreviewUrl);
      setGroupPhotoPreviewUrl(null);
    }
    if (file && file.type.startsWith('image/')) {
      setGroupPhotoPreviewUrl(URL.createObjectURL(file));
    }
    setRequestGroupPhoto(file);
  };

  const handleSourceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    if (sourceFilePreviewUrl) {
      URL.revokeObjectURL(sourceFilePreviewUrl);
      setSourceFilePreviewUrl(null);
    }
    if (file) {
      const canPreview = file.type.startsWith('audio/') || file.type.startsWith('video/');
      if (canPreview) setSourceFilePreviewUrl(URL.createObjectURL(file));
    }
    setSourceFile(file);
  };

  useEffect(() => {
    if (!branchId) {
      setLoadingAvailable(false);
      return;
    }
    fetchAvailable();
  }, [fetchAvailable, branchId]);

  useEffect(() => {
    if (!available || !branchId) return;
    fetchRequests(page);
    fetchCurrentRequest();
    liveSongRequestWebSocketService.connect(branchId);

    const onEvent = () => {
      fetchRequests(page);
      fetchAvailable();
      fetchCurrentRequest();
    };

    const onReconnect = () => {
      fetchRequests(page);
      fetchAvailable();
      fetchCurrentRequest();
    };

    const onCommentNew = (payload: any) => {
      const reqId = payload?.requestId ?? payload?.liveSongRequestId;
      if (reqId) fetchComments(reqId);
      fetchRequests(page);
      fetchCurrentRequest();
    };

    liveSongRequestWebSocketService.on('connect', onReconnect);
    liveSongRequestWebSocketService.on('live_song:config_updated', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:created', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:scheduled', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:rejected', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:current', onEvent);
    liveSongRequestWebSocketService.on('live_song_request:closed', onEvent);
    liveSongRequestWebSocketService.on('live_song_request_comment:new', onCommentNew);

    return () => {
      liveSongRequestWebSocketService.off('connect', onReconnect);
      liveSongRequestWebSocketService.off('live_song:config_updated', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:created', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:scheduled', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:rejected', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:current', onEvent);
      liveSongRequestWebSocketService.off('live_song_request:closed', onEvent);
      liveSongRequestWebSocketService.off('live_song_request_comment:new', onCommentNew);
      liveSongRequestWebSocketService.disconnect();
    };
  }, [available, branchId, page, fetchRequests, fetchAvailable, fetchComments, fetchCurrentRequest]);

  const handleExpandRequest = (requestId: string) => {
    if (expandedRequestId === requestId) {
      setExpandedRequestId(null);
      return;
    }
    setExpandedRequestId(requestId);
    if (!comments[requestId]) fetchComments(requestId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const effectiveSongDescription = (form.songDescription || 'Song').trim();
    if (!branchId || !form.bookingId) {
      setSubmitError('Please ensure you have an arrived table booking.');
      return;
    }
    if (!effectiveSongDescription) {
      setSubmitError('Please enter the song description.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await apiService.createLiveSongRequest(
        branchId,
        {
          bookingId: form.bookingId,
          songDescription: effectiveSongDescription,
          ...(sourceType === 'link' && form.sourceLink.trim() && { sourceLink: form.sourceLink.trim() }),
          ...(form.customerComments.trim() && { customerComments: form.customerComments.trim() }),
          ...(form.fileUrl.trim() && { fileUrl: form.fileUrl.trim() }),
          ...(form.photoUrl.trim() && { photoUrl: form.photoUrl.trim() })
        },
        requestGroupPhoto || undefined,
        sourceType === 'file' && sourceFile ? sourceFile : undefined
      );
      setForm((f) => ({ ...f, songDescription: 'Song', sourceLink: '', customerComments: '', fileUrl: '', photoUrl: '' }));
      if (groupPhotoPreviewUrl) {
        URL.revokeObjectURL(groupPhotoPreviewUrl);
        setGroupPhotoPreviewUrl(null);
      }
      setRequestGroupPhoto(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (sourceFilePreviewUrl) {
        URL.revokeObjectURL(sourceFilePreviewUrl);
        setSourceFilePreviewUrl(null);
      }
      setSourceFile(null);
      if (sourceFileInputRef.current) sourceFileInputRef.current.value = '';
      fetchRequests(page);
      fetchAvailable();
      fetchCurrentRequest();
      setSuccessMessage('Song request is successful');
      setSubmitError(null);
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to submit request.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async (requestId: string) => {
    const text = (newCommentText[requestId] ?? '').trim();
    if (!text) return;
    setPostingComment((prev) => ({ ...prev, [requestId]: true }));
    try {
      await apiService.addLiveSongRequestComment(requestId, { commentText: text });
      setNewCommentText((prev) => ({ ...prev, [requestId]: '' }));
      fetchComments(requestId);
    } catch {
      // ignore
    } finally {
      setPostingComment((prev) => ({ ...prev, [requestId]: false }));
    }
  };

  const handleCurrentCommentImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = Array.from(e.target.files || []);
    setCurrentCommentImageError(null);
    const oversized = newFiles.filter((f) => f.size > COMMENT_IMAGE_MAX_SIZE_BYTES);
    if (oversized.length > 0) {
      setCurrentCommentImageError(`Each file must be under 50MB. ${oversized.length} file(s) too large.`);
      return;
    }
    const invalid = newFiles.filter((f) => !f.type.startsWith('image/') && !f.type.startsWith('video/') && !f.type.startsWith('audio/'));
    if (invalid.length > 0) {
      setCurrentCommentImageError('Only image, video, and audio files are allowed.');
      return;
    }
    setCurrentCommentImages((prev) => {
      const combined = [...prev, ...newFiles].slice(0, COMMENT_IMAGES_MAX);
      if (combined.length > COMMENT_IMAGES_MAX) {
        setCurrentCommentImageError(`Max ${COMMENT_IMAGES_MAX} files allowed. Only the first ${COMMENT_IMAGES_MAX} were added.`);
      }
      return combined;
    });
    if (currentCommentImagesInputRef.current) currentCommentImagesInputRef.current.value = '';
  };

  const handleRemoveCommentImage = (index: number) => {
    setCurrentCommentImages((prev) => prev.filter((_, i) => i !== index));
    setCurrentCommentImageError(null);
  };

  const runYouTubeSearch = useCallback(async (query: string) => {
    const q = query.trim();
    if (!q) {
      setYoutubeSearchResults([]);
      setYoutubeSearchError(null);
      return;
    }
    setYoutubeSearchError(null);
    setYoutubeSearchLoading(true);
    setSelectedYouTubeVideo(null);
    try {
      const results = await searchYouTubeVideos(q, 10);
      setYoutubeSearchResults(results);
    } catch (err: any) {
      setYoutubeSearchError(err?.message ?? 'Search failed');
      setYoutubeSearchResults([]);
    } finally {
      setYoutubeSearchLoading(false);
    }
  }, []);

  const handleYouTubeSearch = useCallback(() => runYouTubeSearch(youtubeSearchQuery), [runYouTubeSearch, youtubeSearchQuery]);

  useEffect(() => {
    const q = youtubeSearchQuery.trim();
    if (!q) {
      setYoutubeSearchResults([]);
      setYoutubeSearchError(null);
      return;
    }
    const debounceMs = 400;
    const t = setTimeout(() => runYouTubeSearch(youtubeSearchQuery), debounceMs);
    return () => clearTimeout(t);
  }, [youtubeSearchQuery, runYouTubeSearch]);

  const handleUseSelectedYouTubeSong = useCallback(() => {
    if (!selectedYouTubeVideo) return;
    const link = getYouTubeWatchUrl(selectedYouTubeVideo.videoId);
    setForm((f) => ({ ...f, songDescription: selectedYouTubeVideo.title, sourceLink: link }));
    setSourceType('link');
    setSelectedYouTubeVideo(null);
    setYoutubeSearchResults([]);
    setYoutubeSearchQuery('');
  }, [selectedYouTubeVideo]);

  const handleAddCurrentComment = async (requestId?: string) => {
    const targetId = requestId ?? currentRequest?.id;
    const text = currentCommentText.trim();
    const hasText = text.length > 0;
    const hasImages = currentCommentImages.length > 0;
    if (!targetId || (!hasText && !hasImages)) return;
    setPostingCurrentComment(true);
    setCommentSentForReview(false);
    setCurrentCommentImageError(null);
    try {
      await apiService.addLiveSongRequestComment(targetId, {
        ...(hasText && { commentText: text }),
        ...(hasImages && { commentImages: currentCommentImages })
      });
      setCurrentCommentText('');
      setCurrentCommentImagePreviewUrls((prev) => {
        prev.forEach((u) => URL.revokeObjectURL(u));
        return [];
      });
      setCurrentCommentImages([]);
      if (currentCommentImagesInputRef.current) currentCommentImagesInputRef.current.value = '';
      setCommentSentForReview(true);
      setTimeout(() => setCommentSentForReview(false), 4000);
      fetchComments(targetId);
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to send.';
      setCurrentCommentImageError(msg);
    } finally {
      setPostingCurrentComment(false);
    }
  };

  const handleRequestAgain = async (req: LiveSongRequest) => {
    if (!branchId || !req.songDescription?.trim()) return;
    const bookingId = req.bookingId || arrivedBookings[0]?.id || '';
    if (!bookingId) {
      setSubmitError('No table booking available to request again. Please ensure you have an arrived booking.');
      return;
    }
    setRequestAgainLoading((prev) => ({ ...prev, [req.id]: true }));
    setSubmitError(null);
    try {
      await apiService.createLiveSongRequest(
        branchId,
        {
          bookingId,
          songDescription: (req.songDescription || '').trim(),
          ...(req.sourceLink?.trim() && { sourceLink: req.sourceLink.trim() }),
          ...(req.customerComments?.trim() && { customerComments: req.customerComments.trim() }),
          ...(req.fileUrl?.trim() && { fileUrl: req.fileUrl.trim() }),
          ...(req.photoUrl?.trim() && { photoUrl: req.photoUrl.trim() })
        }
      );
      fetchRequests(page);
      fetchAvailable();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create request.';
      setSubmitError(msg);
    } finally {
      setRequestAgainLoading((prev) => ({ ...prev, [req.id]: false }));
    }
  };

  if (loadingAvailable) {
    if (inModal) {
      return (
        <div className={styles.loadingPlaceholder} aria-live="polite">
          <p className={styles.muted}>Checking availability…</p>
        </div>
      );
    }
    return null;
  }
  if (!available || arrivedBookings.length === 0) {
    if (inModal) {
      return (
        <p className={styles.muted}>
          Live Song Request is not available for this branch, or you need an arrived table booking to request a song.
        </p>
      );
    }
    return null;
  }

  const selectedTableName = arrivedBookings.find((b) => b.id === form.bookingId)?.tableName || form.bookingId || '—';
  const hasMyRequest = Boolean(myRequest && myRequest.id);
  const allowStatus = ['requested', 'scheduled', 'current'];
  const commentableRequests = (() => {
    const fromMyRequests = requests.filter((r) => allowStatus.includes((r.status || '').toLowerCase()));
    let result = [...fromMyRequests];
    if (myRequest && !result.some((r) => r.id === myRequest.id)) {
      result = [myRequest, ...result];
    }
    if (currentRequest && !result.some((r) => r.id === currentRequest.id)) {
      result = [currentRequest, ...result];
    }
    return result;
  })();

  return (
    <section className={styles.panel} aria-labelledby="live-song-heading">
      {/* 1. Current Song Request Status – first */}
      {commentsEnabled && (
        <div className={styles.sectionNowPlaying}>
          <div className={styles.nowPlayingSection}>
            <h3 className={styles.nowPlayingTitle}>Current Song Request Status</h3>
            {loadingCurrent ? (
              <p className={styles.muted}>Loading…</p>
            ) : currentRequest ? (
              <>
                <p className={styles.nowPlayingSong}>{currentRequest.songDescription || 'Song request'}</p>
                {currentRequest.customerComments && (
                  <p className={styles.nowPlayingDedication}>{currentRequest.customerComments}</p>
                )}
                {(() => {
                  const isOrg = currentRequest.isOrganizer ?? (currentRequest as any).is_organizer;
                  const orgName = currentRequest.organizationName ?? (currentRequest as any).organization_name;
                  const orgLogo = currentRequest.organizerLogoUrl ?? (currentRequest as any).organizer_logo_url;
                  const custName = currentRequest.displayName ?? (currentRequest as any).booking?.customer?.displayName;
                  const custPic = currentRequest.profilePic ?? (currentRequest as any).booking?.customer?.profilePic;
                  const showRequestedBy = isOrg ? orgName || orgLogo : custName || custPic || (currentRequest as any).booking?.customer;
                  if (!showRequestedBy) return null;
                  const displayName = isOrg ? (orgName || 'Organizer') : (custName || 'Customer');
                  const avatarUrl = isOrg ? orgLogo : custPic;
                  return (
                    <div className={styles.nowPlayingRequestedBy}>
                      <span className={styles.nowPlayingRequestedByLabel}>Requested by</span>
                      <div className={styles.nowPlayingRequestedByUser}>
                        {avatarUrl ? (
                          <img src={avatarUrl} alt="" className={styles.nowPlayingAvatar} />
                        ) : (
                          <div className={styles.nowPlayingAvatarPlaceholder}>
                            {(displayName.charAt(0) || '?').toUpperCase()}
                          </div>
                        )}
                        <span className={styles.nowPlayingDisplayName}>{displayName}</span>
                      </div>
                    </div>
                  );
                })()}
                <button
                  type="button"
                  className={styles.nowPlayingSendCommentsBtn}
                  onClick={() => {
                    setSelectedRequestIdForComments(currentRequest.id);
                    if (!comments[currentRequest.id]) fetchComments(currentRequest.id);
                    setCommentsPopupTextOnly(true);
                    setShowSendCommentsPopup(true);
                  }}
                >
                  💬 Send comments
                </button>
              </>
            ) : (
              <p className={styles.muted}>No song is playing right now.</p>
            )}
          </div>
        </div>
      )}

      {/* 2. Live Song Request section */}
      <div className={styles.sectionRequest}>
        <h2 id="live-song-heading" className={styles.heading}>
          🎵 Live Song Request
        </h2>
        {successMessage && (
          <p className={styles.successMessage} role="status">
            {successMessage}
          </p>
        )}
        <form onSubmit={handleSubmit} className={styles.form}>
        {/* Table as label – bookingId from form state is passed to API */}
        <div className={styles.formRow}>
          <p className={styles.tableTitle}>Your table: {selectedTableName}</p>
        </div>

        {/* Search YouTube – hidden for now */}
        {false && (
        <div className={styles.youtubeSearchSection}>
          <p className={styles.youtubeSearchIntro}>Search YouTube in this portal – results update as you type. Pick a song to auto-fill the form.</p>
          <div className={styles.youtubeSearchRow}>
            <input
              type="text"
              className={styles.youtubeSearchInput}
              placeholder="Search for a song on YouTube..."
              value={youtubeSearchQuery}
              onChange={(e) => { setYoutubeSearchQuery(e.target.value); setYoutubeSearchError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleYouTubeSearch()}
              aria-label="YouTube search"
            />
            <button
              type="button"
              className={styles.youtubeSearchBtn}
              onClick={handleYouTubeSearch}
              disabled={youtubeSearchLoading || !youtubeSearchQuery.trim()}
            >
              {youtubeSearchLoading ? 'Searching…' : 'Search'}
            </button>
          </div>
          {youtubeSearchError && <p className={styles.error} role="alert">{youtubeSearchError}</p>}
          {youtubeSearchResults.length > 0 && !selectedYouTubeVideo && (
            <ul className={styles.youtubeSearchResults}>
              {youtubeSearchResults.map((item) => (
                <li key={item.videoId}>
                  <button
                    type="button"
                    className={styles.youtubeSearchResultItem}
                    onClick={() => setSelectedYouTubeVideo(item)}
                  >
                    {item.thumbnailUrl && (
                      <img src={item.thumbnailUrl} alt="" className={styles.youtubeSearchThumb} />
                    )}
                    <span className={styles.youtubeSearchResultText}>
                      <strong>{item.title}</strong>
                      {item.channelTitle && <span className={styles.youtubeSearchChannel}>{item.channelTitle}</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {selectedYouTubeVideo && (
            <div className={styles.youtubeSelectedPreview}>
              <p className={styles.youtubePreviewLabel}>Preview – check the selected song, then click &quot;Use this song&quot; to fill the form</p>
              <div className={styles.youtubePreviewEmbedWrap}>
                <iframe
                  title="YouTube preview"
                  className={styles.youtubeIframe}
                  src={getYouTubeEmbedUrl(selectedYouTubeVideo!.videoId)}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className={styles.youtubeUseSongRow}>
                <button
                  type="button"
                  className={styles.youtubeUseSongBtn}
                  onClick={handleUseSelectedYouTubeSong}
                >
                  Use this song
                </button>
                <button
                  type="button"
                  className={styles.youtubeBackBtn}
                  onClick={() => setSelectedYouTubeVideo(null)}
                >
                  Back to results
                </button>
              </div>
            </div>
          )}
        </div>
        )}

        <div className={styles.formRow}>
          <label htmlFor="live-song-description" className={styles.label}>
            Song description <span className={styles.required}>*</span>
          </label>
          <input
            id="live-song-description"
            type="text"
            className={styles.input}
            placeholder="e.g. Artist - Song title"
            value={form.songDescription && form.songDescription.trim() !== '' ? form.songDescription : 'Song'}
            onChange={(e) => setForm((f) => ({ ...f, songDescription: e.target.value }))}
            required
          />
        </div>
        <div className={styles.formRow}>
          <span className={styles.label}>Source (optional) – choose one</span>
          <div className={styles.sourceTypeToggle}>
            <label className={styles.sourceTypeLabel}>
              <input
                type="radio"
                name="sourceType"
                checked={sourceType === 'link'}
                onChange={() => {
                  setSourceType('link');
                  setSourceFile(null);
                  if (sourceFilePreviewUrl) {
                    URL.revokeObjectURL(sourceFilePreviewUrl);
                    setSourceFilePreviewUrl(null);
                  }
                  if (sourceFileInputRef.current) sourceFileInputRef.current.value = '';
                }}
              />
              <span>Link</span>
            </label>
            <label className={styles.sourceTypeLabel}>
              <input
                type="radio"
                name="sourceType"
                checked={sourceType === 'file'}
                onChange={() => {
                  setSourceType('file');
                  setForm((f) => ({ ...f, sourceLink: '' }));
                }}
              />
              <span>File</span>
            </label>
          </div>
          {sourceType === 'link' && (
            <>
              <p className={styles.sourceLinkOr}>Or paste a YouTube link below</p>
              <div className={styles.sourceLinkRow}>
                <input
                  id="live-song-source"
                  type="url"
                  className={styles.sourceLinkInput}
                  placeholder="Paste YouTube link to verify the song"
                  value={form.sourceLink}
                  onChange={(e) => setForm((f) => ({ ...f, sourceLink: e.target.value }))}
                />
                <button
                  type="button"
                  className={styles.pasteBtn}
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      if (text) setForm((f) => ({ ...f, sourceLink: text }));
                    } catch {
                      // Clipboard API may be blocked; ignore
                    }
                  }}
                >
                  Paste
                </button>
              </div>
              {(() => {
                const ytId = getYouTubeVideoId(form.sourceLink);
                if (!ytId) return null;
                return (
                  <div className={styles.youtubePreview}>
                    <p className={styles.youtubePreviewLabel}>Preview – confirm this is the right song</p>
                    <iframe
                      title="YouTube preview"
                      className={styles.youtubeIframe}
                      src={`https://www.youtube.com/embed/${ytId}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                );
              })()}
            </>
          )}
          {sourceType === 'file' && (
            <>
              <input
                ref={sourceFileInputRef}
                id="live-song-source-file"
                type="file"
                className={styles.fileInput}
                onChange={handleSourceFileChange}
                aria-label="Upload source file (any format)"
              />
              {sourceFile && <span className={styles.fileName}>{sourceFile.name}</span>}
              {sourceFilePreviewUrl && (
                <div className={styles.sourceFilePreview}>
                  <p className={styles.youtubePreviewLabel}>Preview – plays automatically</p>
                  {sourceFile?.type.startsWith('audio/') && (
                    <audio src={sourceFilePreviewUrl} controls autoPlay className={styles.sourceMedia} />
                  )}
                  {sourceFile?.type.startsWith('video/') && (
                    <video src={sourceFilePreviewUrl} controls autoPlay className={styles.sourceMedia} />
                  )}
                  {sourceFile && !sourceFile.type.startsWith('audio/') && !sourceFile.type.startsWith('video/') && (
                    <p className={styles.muted}>Selected: {sourceFile.name} (no preview for this type)</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className={styles.formRow}>
          <label htmlFor="live-song-comments" className={styles.label}>
            Dedication / comments (optional)
          </label>
          <textarea
            id="live-song-comments"
            className={styles.textarea}
            placeholder="e.g. Dedicated to..."
            value={form.customerComments}
            onChange={(e) => setForm((f) => ({ ...f, customerComments: e.target.value }))}
            rows={2}
          />
        </div>
        <div className={styles.formRow}>
          <label htmlFor="live-song-group-photo" className={styles.label}>
            Group photo (optional)
          </label>
          <input
            ref={fileInputRef}
            id="live-song-group-photo"
            type="file"
            accept="image/*"
            className={styles.fileInput}
            onChange={handleGroupPhotoChange}
            aria-label="Upload group photo"
          />
          {requestGroupPhoto && (
            <span className={styles.fileName}>{requestGroupPhoto.name}</span>
          )}
          {groupPhotoPreviewUrl && (
            <div className={styles.thumbnailWrap}>
              <img
                src={groupPhotoPreviewUrl}
                alt="Group photo preview"
                className={styles.thumbnail}
              />
              <span className={styles.thumbnailHint}>Selected photo – submit to upload</span>
            </div>
          )}
        </div>
        <div className={styles.formFooter}>
          {submitError && <p className={styles.error}>{submitError}</p>}
          {submitting && (
            <p className={styles.sendingStatus} aria-live="polite">
              Sending your request…
            </p>
          )}
          {hasMyRequest ? (
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => {
                const targetId = myRequest?.id ?? currentRequest?.id ?? commentableRequests[0]?.id ?? '';
                setSelectedRequestIdForComments(targetId);
                if (targetId && !comments[targetId]) fetchComments(targetId);
                setCommentsPopupTextOnly(false);
                setShowSendCommentsPopup(true);
              }}
            >
              Send comments
            </button>
          ) : (
            <button type="submit" className={styles.submitBtn} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Request song'}
            </button>
          )}
        </div>
      </form>
      </div>

      {/* Send comments popup – portaled to body so it centers by device viewport, not Live Song Request modal */}
      {showSendCommentsPopup && createPortal(
        <div
          className={styles.sendCommentsPopupOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Send comments"
          onClick={() => setShowSendCommentsPopup(false)}
        >
          <div
            className={styles.sendCommentsPopupBox}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.sendCommentsPopupHeader}>
              <h3 className={styles.sendCommentsPopupTitle}>Send comments</h3>
              <button
                type="button"
                className={styles.sendCommentsPopupClose}
                onClick={() => setShowSendCommentsPopup(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className={styles.sendCommentsPopupBody}>
              {commentableRequests.length === 0 ? (
                <p className={styles.muted}>No requests available for comments. (Only requested, scheduled or current can receive comments.)</p>
              ) : (() => {
                const selectedRequest = selectedRequestIdForComments
                  ? commentableRequests.find((r) => r.id === selectedRequestIdForComments) ?? commentableRequests[0]
                  : commentableRequests[0];
                const req = selectedRequest;
                return (
                  <div className={styles.commentsBlock}>
                    <div className={styles.formRow}>
                      <label htmlFor="send-comments-request-popup" className={styles.label}>Request</label>
                      <select
                        id="send-comments-request-popup"
                        className={styles.select}
                        value={selectedRequestIdForComments || req?.id || ''}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSelectedRequestIdForComments(id);
                          if (id && !comments[id]) fetchComments(id);
                        }}
                      >
                        {commentableRequests.map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.songDescription || 'Song request'} ({r.status})
                          </option>
                        ))}
                      </select>
                    </div>
                    <h4 className={styles.commentsTitle}>Comments</h4>
                    {!req ? null : loadingComments[req.id] ? (
                      <p className={styles.muted}>Loading comments…</p>
                    ) : (
                      <>
                        <ul className={styles.commentList}>
                          {(comments[req.id] ?? []).map((c) => (
                            <li key={c.id} className={styles.commentItem}>
                              {c.commentText && <span className={styles.commentText}>{c.commentText}</span>}
                              {c.imageUrls && c.imageUrls.length > 0 && (
                                <div className={styles.commentImages}>
                                  {c.imageUrls.map((url, i) => {
                                    const isVideoUrl = /\.(mp4|webm|mov)(\?|$)/i.test(url);
                                    return (
                                      <a key={i} href={url} target="_blank" rel="noopener noreferrer" className={styles.commentImageLink}>
                                        {isVideoUrl ? (
                                          <video src={url} className={styles.commentImageThumb} muted playsInline preload="metadata" />
                                        ) : (
                                          <img src={url} alt="" className={styles.commentImageThumb} />
                                        )}
                                      </a>
                                    );
                                  })}
                                </div>
                              )}
                              {c.createdAt && (
                                <span className={styles.commentTime}>{new Date(c.createdAt).toLocaleString()}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                        <div className={styles.addCommentRow}>
                          <input
                            type="text"
                            className={styles.commentInput}
                            placeholder={commentsPopupTextOnly ? "Type your comment…" : "Add a comment and/or images/videos/audio…"}
                            value={currentCommentText}
                            onChange={(e) => setCurrentCommentText(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleAddCurrentComment(req.id);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className={styles.commentBtn}
                            onClick={() => handleAddCurrentComment(req.id)}
                            disabled={postingCurrentComment || (!currentCommentText.trim() && (commentsPopupTextOnly || currentCommentImages.length === 0))}
                          >
                            {postingCurrentComment ? 'Sending…' : 'Send'}
                          </button>
                        </div>
                        {!commentsPopupTextOnly && (
                        <div className={styles.commentImagesUpload}>
                          <label className={styles.commentImagesAddFilesLabel}>
                            <input
                              ref={currentCommentImagesInputRef}
                              type="file"
                              accept="image/*,video/*,audio/*"
                              multiple
                              className={styles.commentImagesInput}
                              onChange={handleCurrentCommentImagesChange}
                              aria-label="Add images, videos or audio (max 10, 50MB each)"
                            />
                            Add Files
                          </label>
                          <span className={styles.commentImagesHint}>
                            Max {COMMENT_IMAGES_MAX} images/videos/audio, 50MB each.
                          </span>
                          {currentCommentImages.length > 0 && (
                            <>
                              <span className={styles.commentImagesCount}>{currentCommentImages.length} file(s) selected</span>
                              <button
                                type="button"
                                className={styles.commentImagesPreviewBtn}
                                onClick={() => { setSlideshowPreviewIndex(0); setShowSlideshowPreview(true); }}
                              >
                                Preview
                              </button>
                              <div className={styles.commentImagesSelectedThumbs}>
                                {currentCommentImagePreviewUrls.map((url, i) => {
                                  const file = currentCommentImages[i];
                                  const isVideo = file && file.type.startsWith('video/');
                                  const isAudio = file && file.type.startsWith('audio/');
                                  return (
                                    <span key={i} className={styles.commentImageThumbWrap}>
                                      {isVideo ? (
                                        <video src={url} className={styles.commentImageThumb} muted playsInline preload="metadata" />
                                      ) : isAudio ? (
                                        <span className={styles.commentImageThumbAudio}>Audio</span>
                                      ) : (
                                        <img src={url} alt="" className={styles.commentImageThumb} />
                                      )}
                                      <button
                                        type="button"
                                        className={styles.commentImageThumbRemove}
                                        onClick={() => handleRemoveCommentImage(i)}
                                        aria-label={`Remove file ${i + 1}`}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  );
                                })}
                              </div>
                            </>
                          )}
                        </div>
                        )}
                        {currentCommentImageError && (
                          <p className={styles.error} role="alert">{currentCommentImageError}</p>
                        )}
                        {commentSentForReview && (
                          <p className={styles.commentSentForReview} role="status">
                            Comment sent for review. It will appear after the organizer approves.
                          </p>
                        )}
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className={styles.sectionMyRequests}>
      <div className={styles.listSection}>
        <h3 className={styles.listTitle}>My requests</h3>
        {loadingRequests ? (
          <p className={styles.muted}>Loading...</p>
        ) : requests.length === 0 ? (
          <p className={styles.muted}>No requests yet.</p>
        ) : (
          <ul className={styles.requestList}>
            {requests.map((req, index) => {
              const isExpanded = expandedRequestId === req.id;
              const reqComments = comments[req.id] ?? [];
              const loadingCom = loadingComments[req.id];
              const requestAgainBusy = requestAgainLoading[req.id];
              return (
                <li key={req.id} className={styles.requestItem}>
                  <div className={styles.requestSummaryRow}>
                    <button
                      type="button"
                      className={styles.requestSummary}
                      onClick={() => handleExpandRequest(req.id)}
                      aria-expanded={isExpanded}
                    >
                      <span className={styles.requestSerial}>{(page - 1) * REQUESTS_PAGE_LIMIT + index + 1}.</span>
                      <span className={styles.requestSong}>{req.songDescription || 'Song request'}</span>
                      <span className={styles.requestStatus} data-status={req.status}>
                        {req.status || '—'}
                      </span>
                      <span className={styles.requestExpand}>{isExpanded ? '▼' : '▶'}</span>
                    </button>
                    <button
                      type="button"
                      className={styles.requestAgainBtn}
                      onClick={(e) => { e.stopPropagation(); handleRequestAgain(req); }}
                      disabled={requestAgainBusy}
                      title="Create a new request with the same details"
                    >
                      {requestAgainBusy ? 'Requesting…' : 'Request again'}
                    </button>
                  </div>
                  {/* Preview thumbnail player for every request */}
                  <div className={styles.requestThumbnailPlayer}>
                    {(() => {
                      const link = req.sourceLink ?? req.source_link ?? '';
                      const fileUrl = req.fileUrl ?? req.file_url ?? '';
                      const photoUrl = req.photoUrl ?? req.photo_url ?? '';
                      const ytId = getYouTubeVideoId(link);
                      if (ytId) {
                        return (
                          <div className={styles.requestPreviewWrap}>
                            {
                            /*
                            <a
                              href={getYouTubeWatchUrl(ytId)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.requestPreviewThumb}
                              title="Open in YouTube"
                            >
                              <img
                                src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`}
                                alt="YouTube preview"
                                className={styles.requestPreviewImg}
                              />
                              <span className={styles.requestPreviewPlay} aria-hidden>▶</span>
                            </a>
                            */
                            }
                            <iframe
                              title={`YouTube player for ${req.songDescription || 'song'}`}
                              className={styles.requestPreviewEmbed}
                              src={getYouTubeEmbedUrl(ytId)}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        );
                      }
                      if (fileUrl) {
                        const pathBeforeQuery = fileUrl.split('?')[0];
                        const ext = (pathBeforeQuery.split('.').pop() || '').toLowerCase();
                        const videoExts = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
                        const audioExts = ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac'];
                        const isVideo = videoExts.includes(ext) || /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i.test(fileUrl);
                        const isAudio = audioExts.includes(ext) || /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i.test(fileUrl);
                        if (isVideo) {
                          return (
                            <div className={styles.requestPreviewWrap}>
                              <video src={fileUrl} className={styles.requestPreviewMedia} controls playsInline preload="metadata" />
                            </div>
                          );
                        }
                        if (isAudio) {
                          return (
                            <div className={styles.requestPreviewWrap}>
                              <audio src={fileUrl} className={styles.requestPreviewAudio} controls preload="metadata" />
                            </div>
                          );
                        }
                        return (
                          <div className={styles.requestPreviewWrap}>
                            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className={styles.requestPreviewLink}>
                              Open file
                            </a>
                          </div>
                        );
                      }
                      if (photoUrl) {
                        return (
                          <div className={styles.requestPreviewWrap}>
                            <img src={photoUrl} alt="Request preview" className={styles.requestPreviewPhoto} />
                          </div>
                        );
                      }
                      return (
                        <div className={styles.requestPreviewPlaceholder}>
                          <span className={styles.requestPreviewPlaceholderIcon}>🎵</span>
                          <span>No preview available</span>
                        </div>
                      );
                    })()}
                  </div>
                  {isExpanded && (
                    <div className={styles.requestDetail}>
                      {req.sourceLink && (
                        <p className={styles.requestMeta}>
                          Link: <a href={req.sourceLink} target="_blank" rel="noopener noreferrer">{req.sourceLink}</a>
                        </p>
                      )}
                      {req.customerComments && (
                        <p className={styles.requestMeta}>Dedication: {req.customerComments}</p>
                      )}
                      <div className={styles.commentsBlock}>
                        <h4 className={styles.commentsTitle}>Comments</h4>
                        {loadingCom ? (
                          <p className={styles.muted}>Loading comments...</p>
                        ) : (
                          <>
                            <ul className={styles.commentList}>
                              {reqComments.map((c) => (
                                <li key={c.id} className={styles.commentItem}>
                                  <span className={styles.commentText}>{c.commentText}</span>
                                  {c.createdAt && (
                                    <span className={styles.commentTime}>{new Date(c.createdAt).toLocaleString()}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {requests.length > 0 && (
          <div className={styles.pagination}>
            <span className={styles.paginationInfo}>
              {total > 0
                ? `Page ${page} of ${Math.ceil(total / REQUESTS_PAGE_LIMIT) || 1} (${total} total)`
                : `Page ${page}`}
            </span>
            <div className={styles.paginationButtons}>
              <button
                type="button"
                className={styles.paginationBtn}
                disabled={page <= 1 || loadingRequests}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={styles.paginationBtn}
                disabled={loadingRequests || (total > 0 && page >= Math.ceil(total / REQUESTS_PAGE_LIMIT)) || (total === 0 && requests.length < REQUESTS_PAGE_LIMIT)}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      </div>

      {showSlideshowPreview && currentCommentImages.length > 0 && createPortal(
        <div
          className={styles.slideshowPreviewOverlay}
          role="dialog"
          aria-modal="true"
          aria-label="Preview slides"
          onClick={() => setShowSlideshowPreview(false)}
        >
          <div className={styles.slideshowPreviewBox} onClick={(e) => e.stopPropagation()}>
            <div className={styles.slideshowPreviewHeader}>
              <h3 className={styles.slideshowPreviewTitle}>Preview</h3>
              <button
                type="button"
                className={styles.slideshowPreviewClose}
                onClick={() => setShowSlideshowPreview(false)}
                aria-label="Close preview"
              >
                ×
              </button>
            </div>
            <p className={styles.slideshowPreviewCounter}>
              {slideshowPreviewIndex + 1} / {currentCommentImages.length}
            </p>
            <div className={styles.slideshowPreviewContent}>
              {(() => {
                const file = currentCommentImages[slideshowPreviewIndex];
                const url = currentCommentImagePreviewUrls[slideshowPreviewIndex];
                if (!file || !url) return null;
                const isVideo = file.type.startsWith('video/');
                const isAudio = file.type.startsWith('audio/');
                if (isVideo) {
                  return (
                    <video
                      key={slideshowPreviewIndex}
                      src={url}
                      className={styles.slideshowPreviewMedia}
                      controls
                      playsInline
                      preload="auto"
                    />
                  );
                }
                if (isAudio) {
                  return (
                    <div className={styles.slideshowPreviewAudioWrap}>
                      <p className={styles.slideshowPreviewAudioLabel}>Listen</p>
                      <audio key={slideshowPreviewIndex} src={url} className={styles.slideshowPreviewAudio} controls preload="metadata" />
                    </div>
                  );
                }
                return <img key={slideshowPreviewIndex} src={url} alt="" className={styles.slideshowPreviewMedia} />;
              })()}
            </div>
            <div className={styles.slideshowPreviewNav}>
              <button
                type="button"
                className={styles.slideshowPreviewNavBtn}
                disabled={slideshowPreviewIndex <= 0}
                onClick={() => setSlideshowPreviewIndex((i) => Math.max(0, i - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className={styles.slideshowPreviewNavBtn}
                disabled={slideshowPreviewIndex >= currentCommentImages.length - 1}
                onClick={() => setSlideshowPreviewIndex((i) => Math.min(currentCommentImages.length - 1, i + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </section>
  );
}
